import { formatDate } from '@clim/client-core';
import {
  CONSENT_CONDITIONS,
  CONSENT_CONDITION_LABELS,
  LEGAL_TEXT_KINDS,
  LEGAL_TEXT_KIND_LABELS,
  LEGAL_VARIABLES,
  type ConsentCondition,
  type LegalTextKind,
  type LegalTextRecord,
} from '@clim/domain';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Empty, ErrorBox, Field, Input, Modal, Select, Textarea } from '../../components/ui';
import { useApi, useMutation } from './common';

type Row = LegalTextRecord & { retiredAt: string | null };
interface LegalConfig {
  complete: boolean;
  issues: string[];
  unreviewed: string[];
}
const slug = (v: string) => v.toLowerCase().normalize('NFD').replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 50);
const DOSSIER_VARIABLES = ['dossier.support', 'dossier.hauteur', 'dossier.acces', 'reserve.commentaire', 'client.nom', 'client.adresse', 'commande.date', 'commande.objet'];

/**
 * Textes juridiques (pack juridique) : CGV, cases à cocher, réserves, RGPD, formulaire de
 * rétractation. Chaque texte / case peut être ACTIVÉ ou DÉSACTIVÉ (il n'est alors plus présenté
 * au client) ; toute modification du contenu publie une NOUVELLE version, les contrats signés
 * gardent la leur.
 */
export function LegalTextsAdmin() {
  const list = useApi<Row[]>('/v1/admin/legal-texts');
  const config = useApi<LegalConfig>('/v1/admin/legal-config');
  const m = useMutation();
  const [editing, setEditing] = useState<Partial<Row> | null>(null);
  const [history, setHistory] = useState<string | null>(null);
  const rows = list.data ?? [];
  const current = rows.filter((r) => !r.retiredAt);
  const reload = () => {
    list.reload();
    config.reload();
  };
  const toggle = (r: Row) => void m.run('POST', `/v1/admin/legal-texts/${r.id}/active`, { isActive: !r.isActive }).then(reload, () => undefined);

  return (
    <div>
      <ErrorBox message={list.error ?? m.error} />
      {config.data && !config.data.complete && (
        <div className="card legal-alert" role="alert">
          <h2>⚠ CONFIGURATION JURIDIQUE INCOMPLÈTE</h2>
          <p>
            À compléter avant la mise en production (en production, la signature est bloquée tant que cette liste n’est pas vide). Les coordonnées se
            renseignent dans <Link to="../parametres">Entreprise → Mentions légales</Link>.
          </p>
          <ul>
            {config.data.issues.map((i) => (
              <li key={i}>{i}</li>
            ))}
          </ul>
        </div>
      )}
      {config.data?.complete && <p className="badge tone-success">✓ Configuration juridique complète</p>}
      {config.data && config.data.unreviewed.length > 0 && (
        <p className="muted">
          {config.data.unreviewed.length} texte(s) actif(s) non encore marqué(s) « validé juridiquement » : publiez une version validée (bloquant en production).
        </p>
      )}

      {LEGAL_TEXT_KINDS.map((kind) => (
        <div key={kind} className="card">
          <div className="card-head">
            <h2>{LEGAL_TEXT_KIND_LABELS[kind]}</h2>
            <button className="btn small primary" onClick={() => setEditing({ kind, code: '', title: '', body: '', required: false, reviewed: false, sortOrder: 0, condition: 'always', isActive: true })}>
              + Nouveau
            </button>
          </div>
          {current.filter((r) => r.kind === kind).length === 0 && <Empty>Aucun texte.</Empty>}
          <ul className="plain">
            {current
              .filter((r) => r.kind === kind)
              .sort((a, b) => a.sortOrder - b.sortOrder || a.code.localeCompare(b.code))
              .map((r) => (
                <li key={r.id} className={`row legal-row${r.isActive ? '' : ' disabled'}`}>
                  <label className="switch" title={r.isActive ? 'Affiché au client' : 'Masqué'}>
                    <input type="checkbox" checked={r.isActive} onChange={() => toggle(r)} aria-label={`${r.isActive ? 'Désactiver' : 'Activer'} « ${r.title} »`} />
                    <span>{r.isActive ? 'Activé' : 'Désactivé'}</span>
                  </label>
                  <div className="row-main">
                    <strong>
                      {r.title} <span className="muted">· v{r.textVersion}</span>
                    </strong>
                    <span className="muted">
                      {r.reviewed ? '✓ validé juridiquement' : '⚠ non validé juridiquement'}
                      {kind === 'consent' && ` · ${r.required ? 'obligatoire' : 'facultative'} · ${CONSENT_CONDITION_LABELS[r.condition]}`}
                      {kind === 'reserve' && `${r.requiresComment ? ' · commentaire obligatoire' : ''}${r.requiresPhoto ? ' · photo exigée' : ''}`}
                      {r.legalBasis && ` · ${r.legalBasis}`} · publié le {formatDate(r.createdAt)}
                    </span>
                  </div>
                  <button className="btn small" onClick={() => setEditing(r)}>
                    Modifier (nouvelle version)
                  </button>
                  <button className="btn small ghost" onClick={() => setHistory(`${r.kind}:${r.code}`)}>
                    Historique
                  </button>
                </li>
              ))}
          </ul>
        </div>
      ))}

      {editing && (
        <PublishDialog
          initial={editing}
          onClose={(saved) => {
            setEditing(null);
            if (saved) reload();
          }}
        />
      )}
      {history && (
        <Modal title="Historique des versions" onClose={() => setHistory(null)}>
          <ol className="timeline">
            {rows
              .filter((r) => `${r.kind}:${r.code}` === history)
              .map((r) => (
                <li key={r.id}>
                  <strong>
                    v{r.textVersion} — {r.title}
                  </strong>{' '}
                  <span className="muted">
                    publiée le {formatDate(r.createdAt)}
                    {r.retiredAt && `, remplacée le ${formatDate(r.retiredAt)}`} · {r.reviewed ? 'validée' : 'non validée'}
                  </span>
                  <div className="pre small">{r.body}</div>
                </li>
              ))}
          </ol>
        </Modal>
      )}
    </div>
  );
}

function PublishDialog({ initial, onClose }: { initial: Partial<Row>; onClose(saved: boolean): void }) {
  const m = useMutation();
  const isNew = !initial.id;
  const [t, setT] = useState({
    kind: (initial.kind ?? 'terms') as LegalTextKind,
    code: initial.code ?? '',
    title: initial.title ?? '',
    body: initial.body ?? '',
    required: initial.required ?? false,
    reviewed: false,
    isActive: initial.isActive ?? true,
    condition: (initial.condition ?? 'always') as ConsentCondition,
    legalBasis: initial.legalBasis ?? '',
    category: initial.category ?? '',
    requiresComment: initial.requiresComment ?? false,
    requiresPhoto: initial.requiresPhoto ?? false,
    sortOrder: initial.sortOrder ?? 0,
  });
  const save = () => m.run('POST', '/v1/admin/legal-texts', t).then(() => onClose(true), () => undefined);
  return (
    <Modal title={isNew ? 'Nouveau texte' : `Nouvelle version de « ${initial.title} »`} onClose={() => onClose(false)}>
      <div className="form">
        {isNew && (
          <Field label="Type">
            <Select value={t.kind} onChange={(e) => setT({ ...t, kind: e.target.value as LegalTextKind })}>
              {LEGAL_TEXT_KINDS.map((k) => (
                <option key={k} value={k}>
                  {LEGAL_TEXT_KIND_LABELS[k]}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <Field label="Titre">
          <Input value={t.title} onChange={(e) => setT({ ...t, title: e.target.value, code: isNew ? slug(e.target.value) : t.code })} />
        </Field>
        <Field label="Texte" hint="Variables remplacées automatiquement, ex. {{reclamations.email}} — liste ci-dessous">
          <Textarea rows={12} value={t.body} onChange={(e) => setT({ ...t, body: e.target.value })} />
        </Field>
        <details>
          <summary className="muted">Variables disponibles</summary>
          <p className="small">
            Entreprise (Administration → Entreprise) :{' '}
            {Object.keys(LEGAL_VARIABLES).map((v) => (
              <code key={v}>{`{{${v}}} `}</code>
            ))}
            <br />
            Dossier : {DOSSIER_VARIABLES.map((v) => (
              <code key={v}>{`{{${v}}} `}</code>
            ))}
          </p>
        </details>
        {t.kind === 'consent' && (
          <>
            <Field label="Affichage">
              <Select value={t.condition} onChange={(e) => setT({ ...t, condition: e.target.value as ConsentCondition })}>
                {CONSENT_CONDITIONS.map((c) => (
                  <option key={c} value={c}>
                    {CONSENT_CONDITION_LABELS[c]}
                  </option>
                ))}
              </Select>
            </Field>
            <label className="check">
              <input type="checkbox" checked={t.required} onChange={(e) => setT({ ...t, required: e.target.checked })} />
              Case obligatoire pour signer (lorsqu’elle est affichée)
            </label>
            <label className="check">
              <input type="checkbox" checked={t.category === 'marketing'} onChange={(e) => setT({ ...t, category: e.target.checked ? 'marketing' : '' })} />
              Consentement aux communications commerciales (mis à jour sur la fiche client)
            </label>
            <Field label="Base légale / fondement">
              <Input value={t.legalBasis} onChange={(e) => setT({ ...t, legalBasis: e.target.value })} placeholder="ex. Exécution du contrat, Consentement…" />
            </Field>
          </>
        )}
        {t.kind === 'reserve' && (
          <>
            <label className="check">
              <input type="checkbox" checked={t.requiresComment} onChange={(e) => setT({ ...t, requiresComment: e.target.checked })} />
              Commentaire obligatoire (variable {'{{reserve.commentaire}}'})
            </label>
            <label className="check">
              <input type="checkbox" checked={t.requiresPhoto} onChange={(e) => setT({ ...t, requiresPhoto: e.target.checked })} />
              Photo exigée avant signature
            </label>
          </>
        )}
        <label className="check">
          <input type="checkbox" checked={t.isActive} onChange={(e) => setT({ ...t, isActive: e.target.checked })} />
          Activé (présenté au client)
        </label>
        <label className="check">
          <input type="checkbox" checked={t.reviewed} onChange={(e) => setT({ ...t, reviewed: e.target.checked })} />
          Texte validé juridiquement
        </label>
        <ErrorBox message={m.error} />
        <div className="actions">
          <button className="btn" onClick={() => onClose(false)}>
            Annuler
          </button>
          <button className="btn primary" disabled={!t.title.trim() || !t.body.trim() || !t.code} onClick={() => void save()}>
            Publier
          </button>
        </div>
      </div>
    </Modal>
  );
}
